/**
 * Statement of Applicability (SoA) Matrix Page — M6.2 Phase 2
 * Internal governance view for compliance mapping
 */
import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { API, useAuth } from '../App';
import QMSLayout from '../components/QMSLayout';
import {
  Search, Filter, CheckCircle2, AlertCircle, Info, XCircle,
  ChevronDown, ChevronRight, Shield, FileCheck, Clock, User,
  BookOpen, Loader2, Plus, Download, Eye, Edit2, RefreshCw,
  AlertTriangle, Target, ClipboardList, Calendar, X
} from 'lucide-react';

// Applicability badge component
const ApplicabilityBadge = ({ status, t }) => {
  const configs = {
    applicable: { bg: 'bg-emerald-500/15', text: 'text-emerald-400', icon: CheckCircle2, label: t('governance:soa.applicableBadge') },
    partially_applicable: { bg: 'bg-amber-500/15', text: 'text-amber-400', icon: AlertCircle, label: t('governance:soa.partiallyApplicable') },
    informational: { bg: 'bg-blue-500/15', text: 'text-blue-400', icon: Info, label: t('governance:soa.informational') },
    not_applicable: { bg: 'bg-gray-500/15', text: 'text-gray-400', icon: XCircle, label: t('governance:soa.notApplicable') }
  };
  
  const config = configs[status] || configs.informational;
  const Icon = config.icon;
  
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${config.bg} ${config.text}`}>
      <Icon size={12} />
      {config.label}
    </span>
  );
};

// Gap status badge component
const GapBadge = ({ status, t }) => {
  const configs = {
    none: { bg: 'bg-emerald-500/15', text: 'text-emerald-400', icon: CheckCircle2, label: t('governance:soa.noGapBadge') },
    minor_gap: { bg: 'bg-amber-500/15', text: 'text-amber-400', icon: AlertCircle, label: t('governance:soa.minorGap') },
    major_gap: { bg: 'bg-red-500/15', text: 'text-red-400', icon: AlertTriangle, label: t('governance:soa.majorGap') },
    planned: { bg: 'bg-blue-500/15', text: 'text-blue-400', icon: Target, label: t('governance:soa.planned') },
    deferred: { bg: 'bg-gray-500/15', text: 'text-gray-400', icon: Clock, label: t('governance:soa.deferred') }
  };
  
  const config = configs[status] || configs.none;
  const Icon = config.icon;
  
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${config.bg} ${config.text}`}>
      <Icon size={12} />
      {config.label}
    </span>
  );
};

// Review frequency badge
const ReviewFrequencyBadge = ({ frequency, t }) => {
  const labels = {
    monthly: t('governance:soa.monthly'),
    quarterly: t('governance:soa.quarterly'),
    semi_annual: t('governance:soa.semiAnnual'),
    annual: t('governance:soa.annual'),
    biennial: t('governance:soa.biennial'),
    as_needed: t('governance:soa.asNeeded')
  };
  
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-purple-500/15 text-purple-400">
      <Calendar size={12} />
      {labels[frequency] || frequency}
    </span>
  );
};

// SoA Entry Detail Modal
const SoADetailModal = ({ entry, onClose, onMarkReviewed, isAdmin, t }) => {
  if (!entry) return null;
  
  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#1A1D24] border border-[rgba(255,255,255,0.07)] rounded-lg max-w-3xl w-full max-h-[90vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 border-b border-[rgba(255,255,255,0.07)]">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="text-white font-semibold text-lg mb-1">{entry.standard_title}</h2>
              <p className="text-[#8B95A5] text-sm">{t('governance:soa.clause')}: {entry.clause_or_control}</p>
            </div>
            <button onClick={onClose} className="text-[#8B95A5] hover:text-white transition-colors">
              <X size={24} />
            </button>
          </div>
        </div>
        
        <div className="p-6 overflow-y-auto max-h-[65vh]">
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div>
              <label className="text-[#8B95A5] text-xs uppercase tracking-wider block mb-1">{t('governance:soa.controlArea')}</label>
              <p className="text-white text-sm">{entry.control_area}</p>
            </div>
            <div>
              <label className="text-[#8B95A5] text-xs uppercase tracking-wider block mb-1">{t('governance:soa.owner')}</label>
              <p className="text-white text-sm flex items-center gap-1">
                <User size={14} className="text-[#8B95A5]" />
                {entry.owner_role}
              </p>
            </div>
            <div>
              <label className="text-[#8B95A5] text-xs uppercase tracking-wider block mb-1">{t('governance:soa.applicabilityLabel')}</label>
              <ApplicabilityBadge status={entry.applicability_status} t={t} />
            </div>
            <div>
              <label className="text-[#8B95A5] text-xs uppercase tracking-wider block mb-1">{t('governance:soa.gapStatusLabel')}</label>
              <GapBadge status={entry.gap_status} t={t} />
            </div>
            <div>
              <label className="text-[#8B95A5] text-xs uppercase tracking-wider block mb-1">{t('governance:soa.reviewFrequencyLabel')}</label>
              <ReviewFrequencyBadge frequency={entry.review_frequency} t={t} />
            </div>
            <div>
              <label className="text-[#8B95A5] text-xs uppercase tracking-wider block mb-1">{t('governance:soa.lastReviewed')}</label>
              <p className="text-white text-sm">
                {entry.last_reviewed_at 
                  ? new Date(entry.last_reviewed_at).toLocaleDateString()
                  : t('governance:soa.notReviewed')}
              </p>
            </div>
          </div>
          
          <div className="mb-6">
            <label className="text-[#8B95A5] text-xs uppercase tracking-wider block mb-2">{t('governance:soa.rationale')}</label>
            <p className="text-white text-sm leading-relaxed bg-[#0B0E14] rounded p-3">{entry.rationale}</p>
          </div>
          
          {entry.implemented_control && (
            <div className="mb-6">
              <label className="text-[#8B95A5] text-xs uppercase tracking-wider block mb-2">{t('governance:soa.implementedControl')}</label>
              <p className="text-white text-sm leading-relaxed bg-[#0B0E14] rounded p-3">{entry.implemented_control}</p>
            </div>
          )}
          
          {entry.evidence_artifacts && entry.evidence_artifacts.length > 0 && (
            <div className="mb-6">
              <label className="text-[#8B95A5] text-xs uppercase tracking-wider block mb-2">{t('governance:soa.evidence')}</label>
              <div className="flex flex-wrap gap-2">
                {entry.evidence_artifacts.map((artifact, idx) => (
                  <span key={idx} className="px-2 py-1 bg-[#2D313D] text-[#8B95A5] text-xs rounded">
                    {artifact}
                  </span>
                ))}
              </div>
            </div>
          )}
          
          {entry.action_needed && (
            <div className="mb-6 bg-amber-500/10 border border-amber-500/20 rounded-lg p-4">
              <label className="text-amber-400 text-xs uppercase tracking-wider block mb-2 flex items-center gap-1">
                <AlertTriangle size={14} />
                Action Needed
              </label>
              <p className="text-white text-sm">{entry.action_needed}</p>
            </div>
          )}
        </div>
        
        <div className="p-4 border-t border-[rgba(255,255,255,0.07)] flex justify-end gap-3">
          {(isAdmin) && (
            <button
              onClick={() => onMarkReviewed(entry.soa_id)}
              className="px-4 py-2 text-emerald-400 hover:bg-emerald-500/10 rounded transition-colors text-sm flex items-center gap-2"
            >
              <RefreshCw size={14} />
              {t('governance:soa.markReviewed')}
            </button>
          )}
          <button
            onClick={onClose}
            className="px-4 py-2 bg-[#2D313D] text-white hover:bg-[#3D4150] rounded transition-colors text-sm"
          >
            {t('common:actions.close')}
          </button>
        </div>
      </div>
    </div>
  );
};

// Main SoA Matrix Page
const SoAMatrixPage = () => {
  const { t } = useTranslation(['governance', 'common']);
  const { isAdmin, isAuditor, user } = useAuth();
  
  const [entries, setEntries] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [applicabilityFilter, setApplicabilityFilter] = useState('all');
  const [gapFilter, setGapFilter] = useState('all');
  
  // Detail modal
  const [selectedEntry, setSelectedEntry] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [entriesRes, statsRes] = await Promise.all([
        fetch(`${API}/governance/soa`, { credentials: 'include' }),
        fetch(`${API}/governance/soa/stats`, { credentials: 'include' })
      ]);
      
      if (entriesRes.status === 403) {
        setError(t('governance:soa.errorAccess'));
        setLoading(false);
        return;
      }
      
      if (!entriesRes.ok || !statsRes.ok) {
        throw new Error(t('governance:soa.errorLoad'));
      }
      
      const [entriesData, statsData] = await Promise.all([
        entriesRes.json(),
        statsRes.json()
      ]);
      
      setEntries(entriesData);
      setStats(statsData);
      setError(null);
    } catch (err) {
      console.error('Error loading SoA matrix:', err);
      setError(t('governance:soa.errorLoad'));
    } finally {
      setLoading(false);
    }
  };

  const handleMarkReviewed = async (soaId) => {
    try {
      const res = await fetch(`${API}/governance/soa/${soaId}/review`, {
        method: 'POST',
        credentials: 'include'
      });
      
      if (res.ok) {
        // Refresh data
        loadData();
        setSelectedEntry(null);
      }
    } catch (err) {
      console.error('Error marking reviewed:', err);
    }
  };

  const handleExport = async (format) => {
    try {
      const res = await fetch(`${API}/governance/soa/export?format=${format}`, {
        credentials: 'include'
      });
      
      if (res.ok) {
        if (format === 'csv') {
          const blob = await res.blob();
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = 'soa_matrix_export.csv';
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
          a.remove();
        } else {
          const data = await res.json();
          const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = 'soa_matrix_export.json';
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
          a.remove();
        }
      }
    } catch (err) {
      console.error('Error exporting:', err);
    }
  };

  // Filtered entries
  const filteredEntries = useMemo(() => {
    return entries.filter(entry => {
      if (applicabilityFilter !== 'all' && entry.applicability_status !== applicabilityFilter) return false;
      if (gapFilter !== 'all' && entry.gap_status !== gapFilter) return false;
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        return (
          entry.standard_title.toLowerCase().includes(q) ||
          entry.control_area.toLowerCase().includes(q) ||
          entry.clause_or_control.toLowerCase().includes(q) ||
          entry.rationale.toLowerCase().includes(q)
        );
      }
      return true;
    });
  }, [entries, applicabilityFilter, gapFilter, searchQuery]);

  // Access check
  if (!isAdmin && !isAuditor) {
    return (
      <QMSLayout title={t('governance:soa.title')}>
        <div className="qms-card">
          <div className="qms-card-body p-8 text-center">
            <Shield className="mx-auto mb-4 text-red-400" size={48} />
            <h3 className="text-white font-semibold mb-2">{t('governance:soa.accessRestricted')}</h3>
            <p className="text-[#8B95A5] text-sm">
              {t('governance:soa.accessRestrictedDesc')}
            </p>
          </div>
        </div>
      </QMSLayout>
    );
  }

  if (loading) {
    return (
      <QMSLayout title={t('governance:soa.title')}>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-cyan-400" />
        </div>
      </QMSLayout>
    );
  }

  return (
    <QMSLayout title={t('governance:soa.title')}>
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <ClipboardList className="text-[#36A9E1]" size={28} />
              <h1 className="font-['Syne'] text-2xl font-bold text-white">{t('governance:soa.pageTitle')}</h1>
            </div>
            <p className="text-[#8B95A5] text-sm">
              {t('governance:soa.pageDescription')}
            </p>
          </div>
          {isAdmin && (
            <div className="flex items-center gap-2">
              <button
                onClick={() => handleExport('csv')}
                className="px-3 py-2 bg-[#2D313D] text-white hover:bg-[#3D4150] rounded text-sm flex items-center gap-2 transition-colors"
              >
                <Download size={14} />
                {t('governance:soa.exportCsv')}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          <div className="qms-card p-4">
            <div className="text-[#8B95A5] text-xs uppercase tracking-wider mb-1">{t('governance:soa.totalEntries')}</div>
            <div className="text-white text-2xl font-bold">{stats.total}</div>
          </div>
          <div className="qms-card p-4">
            <div className="text-[#8B95A5] text-xs uppercase tracking-wider mb-1">{t('governance:soa.applicable')}</div>
            <div className="text-emerald-400 text-2xl font-bold">{stats.applicable}</div>
          </div>
          <div className="qms-card p-4">
            <div className="text-[#8B95A5] text-xs uppercase tracking-wider mb-1">{t('governance:soa.partially')}</div>
            <div className="text-amber-400 text-2xl font-bold">{stats.partially_applicable}</div>
          </div>
          <div className="qms-card p-4">
            <div className="text-[#8B95A5] text-xs uppercase tracking-wider mb-1">{t('governance:soa.noGap')}</div>
            <div className="text-emerald-400 text-2xl font-bold">{stats.no_gap}</div>
          </div>
          <div className="qms-card p-4">
            <div className="text-[#8B95A5] text-xs uppercase tracking-wider mb-1">{t('governance:soa.gaps')}</div>
            <div className="text-red-400 text-2xl font-bold">{(stats.minor_gap || 0) + (stats.major_gap || 0)}</div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="qms-card mb-6">
        <div className="qms-card-body p-4">
          <div className="flex flex-wrap items-center gap-4">
            {/* Search */}
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8B95A5]" size={16} />
              <input
                type="text"
                placeholder={t('governance:soa.searchPlaceholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-[#0B0E14] border border-[rgba(255,255,255,0.07)] rounded text-white text-sm placeholder-[#8B95A5] focus:border-[#36A9E1] focus:outline-none"
              />
            </div>
            
            {/* Applicability Filter */}
            <select
              value={applicabilityFilter}
              onChange={(e) => setApplicabilityFilter(e.target.value)}
              className="px-3 py-2 bg-[#0B0E14] border border-[rgba(255,255,255,0.07)] rounded text-white text-sm focus:border-[#36A9E1] focus:outline-none"
            >
              <option value="all">{t('governance:soa.allApplicability')}</option>
              <option value="applicable">{t('governance:soa.applicableBadge')}</option>
              <option value="partially_applicable">{t('governance:soa.partiallyApplicable')}</option>
              <option value="informational">{t('governance:soa.informational')}</option>
              <option value="not_applicable">{t('governance:soa.notApplicable')}</option>
            </select>
            
            {/* Gap Filter */}
            <select
              value={gapFilter}
              onChange={(e) => setGapFilter(e.target.value)}
              className="px-3 py-2 bg-[#0B0E14] border border-[rgba(255,255,255,0.07)] rounded text-white text-sm focus:border-[#36A9E1] focus:outline-none"
            >
              <option value="all">{t('governance:soa.allGapStatus')}</option>
              <option value="none">{t('governance:soa.noGapBadge')}</option>
              <option value="minor_gap">{t('governance:soa.minorGap')}</option>
              <option value="major_gap">{t('governance:soa.majorGap')}</option>
              <option value="planned">{t('governance:soa.planned')}</option>
              <option value="deferred">{t('governance:soa.deferred')}</option>
            </select>
          </div>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="qms-card border-red-500/30 mb-6">
          <div className="qms-card-body p-4 text-red-400 text-sm flex items-center gap-2">
            <AlertTriangle size={16} />
            {error}
          </div>
        </div>
      )}

      {/* SoA Table */}
      <div className="qms-card">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-[rgba(255,255,255,0.07)]">
                <th className="text-left p-4 text-[#8B95A5] text-xs uppercase tracking-wider font-medium">Standard / Clause</th>
                <th className="text-left p-4 text-[#8B95A5] text-xs uppercase tracking-wider font-medium">Control Area</th>
                <th className="text-left p-4 text-[#8B95A5] text-xs uppercase tracking-wider font-medium">Applicability</th>
                <th className="text-left p-4 text-[#8B95A5] text-xs uppercase tracking-wider font-medium">Gap Status</th>
                <th className="text-left p-4 text-[#8B95A5] text-xs uppercase tracking-wider font-medium">Owner</th>
                <th className="text-left p-4 text-[#8B95A5] text-xs uppercase tracking-wider font-medium">Review</th>
                <th className="text-center p-4 text-[#8B95A5] text-xs uppercase tracking-wider font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredEntries.map((entry) => (
                <tr 
                  key={entry.soa_id} 
                  className="border-b border-[rgba(255,255,255,0.04)] hover:bg-[rgba(255,255,255,0.02)] transition-colors cursor-pointer"
                  onClick={() => setSelectedEntry(entry)}
                >
                  <td className="p-4">
                    <div className="text-white text-sm font-medium">{entry.standard_title}</div>
                    <div className="text-[#8B95A5] text-xs">§ {entry.clause_or_control}</div>
                  </td>
                  <td className="p-4">
                    <div className="text-white text-sm">{entry.control_area}</div>
                  </td>
                  <td className="p-4">
                    <ApplicabilityBadge status={entry.applicability_status} t={t} />
                  </td>
                  <td className="p-4">
                    <GapBadge status={entry.gap_status} t={t} />
                  </td>
                  <td className="p-4">
                    <div className="text-[#8B95A5] text-sm flex items-center gap-1">
                      <User size={12} />
                      {entry.owner_role}
                    </div>
                  </td>
                  <td className="p-4">
                    <ReviewFrequencyBadge frequency={entry.review_frequency} t={t} />
                  </td>
                  <td className="p-4 text-center">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedEntry(entry);
                      }}
                      className="p-2 text-[#8B95A5] hover:text-[#36A9E1] hover:bg-[#36A9E1]/10 rounded transition-colors"
                    >
                      <Eye size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {/* Empty State */}
        {filteredEntries.length === 0 && !loading && !error && (
          <div className="p-8 text-center">
            <ClipboardList className="mx-auto mb-4 text-[#8B95A5]" size={48} />
            <h3 className="text-white font-semibold mb-2">{t('governance:soa.noEntries')}</h3>
            <p className="text-[#8B95A5] text-sm">
              {searchQuery || applicabilityFilter !== 'all' || gapFilter !== 'all'
                ? t('governance:soa.adjustFilters')
                : t('governance:soa.noEntriesYet')}
            </p>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {selectedEntry && (
        <SoADetailModal
          entry={selectedEntry}
          onClose={() => setSelectedEntry(null)}
          onMarkReviewed={handleMarkReviewed}
          isAdmin={isAdmin}
          t={t}
        />
      )}
    </QMSLayout>
  );
};

export default SoAMatrixPage;
